/* eslint-disable no-param-reassign */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useGetQuotableSystemsQuery,
  useGetQuoteDataByInstanceQuery
} from 'features/api/project-api';
import { combinedCompatibleTotal } from 'features/structure/structure-slice';
import { setQuotableSystems } from 'features/systems';
import { setPageProgress } from 'features/ui';
import useHybridLeftoverSpaceIds from 'hooks/use-hybrid-leftover-space-ids';
import { useIsSystemQuoted } from 'hooks/use-quoted-unique-ids';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { produce } from 'immer';
import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { isChooseSystemsPrerequisiteComplete } from 'utils/choose-systems-gate';
import { usesDuctedAccessoryRules } from 'utils/ducted-accessory-rules';
import { selectIsDuctedDesignPending } from 'utils/ducted-design';
import {
  isEquipmentPreferencesComplete,
  isPtacExistingEquipmentComplete
} from 'utils/equipment-preferences-complete';
import {
  componentsHaveRequiredAttributes,
  getExistingEquipmentComponents
} from 'utils/existing-equipment';
import {
  asksHasExistingEquipment,
  equipmentPreferencesPageVisible,
  existingEquipmentPageVisible,
  HAS_EXISTING_EQUIPMENT_NO,
  hasExistingEquipmentAnswered
} from 'utils/existing-equipment-page-visibility';
import { existingEquipmentHasInventory } from 'utils/existing-equipment-steps';
import { isNotEmpty } from 'utils/helpers';
import { selectFetchedConfigurations } from 'utils/pathway-configurations';
import {
  getProjectSettingValue,
  isDuctedPathway,
  isPtacPreference
} from 'utils/project';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { generateStructureAccuracyData } from 'utils/ui';

export default function PageProgressUpdater() {
  const dispatch = useAppDispatch();
  const leftoverSpaceIds = useHybridLeftoverSpaceIds();
  const { configurations } = useAppSelector((state) => state.configurations);
  const {
    structure,
    structureTypeId,
    structureSubtypeId,
    existingSystems,
    structureAttachments,
    compatibleResultsBySystem,
    recommendationSelectionsBySystem,
    recommendationSelectionsLoaded,
    wantCompositionPicksBySystem
  } = useAppSelector((state) => state.structure);
  const {
    selectedSystems,
    selectedDuctedSystems,
    quotableSystems,
    systemsToCompare
  } = useAppSelector((state) => state.systems);
  const fetchedConfigurations = useAppSelector(
    (state) => selectFetchedConfigurations(state, leftoverSpaceIds),
    shallowEqual
  );
  const ductedDesignPending = useAppSelector(selectIsDuctedDesignPending);
  const systemDesignLocked = isSystemDesignLocked(useSystemDesignBypassMode());
  const {
    locationData,
    attomData,
    projectSettings,
    instanceUniqueId,
    totalBTUs
  } = useAppSelector((state) => state.project);
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const { pageProgress, manuallyCompletedSteps } = useAppSelector(
    (state) => state.ui
  );

  const uniqueIds = useMemo(
    () => selectedSystems.map((s) => s.uniqueId),
    [selectedSystems]
  );

  const { data: quotableSystemsData } = useGetQuotableSystemsQuery(uniqueIds, {
    skip: uniqueIds.length === 0
  });

  const { data: quotedSystemsData } = useGetQuoteDataByInstanceQuery(
    {
      instanceUniqueId: instanceUniqueId!,
      structureUniqueId: structureUniqueId!
    },
    { skip: !instanceUniqueId || !structureUniqueId }
  );

  const isSystemQuoted = useIsSystemQuoted();

  useEffect(() => {
    if (quotableSystemsData) {
      dispatch(setQuotableSystems(quotableSystemsData));
    }
  }, [quotableSystemsData, dispatch]);

  useEffect(() => {
    // project page
    const systemPreference = getProjectSettingValue('system_preference');
    const projectType = getProjectSettingValue('project_type');
    const asksExistingEquipment = asksHasExistingEquipment(systemPreference);
    const showExistingEquipmentPage = existingEquipmentPageVisible(
      systemPreference,
      projectType
    );

    let extraProjectRequirements: boolean[] = [];
    if (asksExistingEquipment) {
      extraProjectRequirements = [hasExistingEquipmentAnswered(projectType)];
    } else if (!isDuctedPathway()) {
      extraProjectRequirements = [isNotEmpty(projectType)];
    }

    const projectRequirementsMet = [
      isNotEmpty(systemPreference),
      ...extraProjectRequirements,
      isNotEmpty(attomData) ||
        (isNotEmpty(locationData?.zipcode) &&
          isNotEmpty(getProjectSettingValue('year_built'))),
      isNotEmpty(getProjectSettingValue('primary_or_detached_structure')),
      isNotEmpty(structureTypeId),
      isNotEmpty(structureSubtypeId)
    ];

    const existingEquipmentComponents =
      getExistingEquipmentComponents(existingSystems);
    const existingEquipmentInventoryGate = isPtacPreference()
      ? recommendationSelectionsLoaded &&
        isPtacExistingEquipmentComplete(
          existingSystems,
          recommendationSelectionsBySystem
        )
      : existingEquipmentHasInventory(existingEquipmentComponents.length) &&
        componentsHaveRequiredAttributes(existingEquipmentComponents);
    const equipmentPreferencesGate = isEquipmentPreferencesComplete({
      recommendationSelectionsLoaded,
      existingSystems,
      recommendationSelectionsBySystem,
      compositionPicksBySystem: wantCompositionPicksBySystem,
      coolingBtu: totalBTUs.cooling
    });

    // structure page (derived from the same step definitions as the sidebar)
    const structureSections = generateStructureAccuracyData().sections;
    const structureCompletedCount = structureSections.filter(
      (s) => s.requirementsMet
    ).length;
    const structureFraction =
      structureSections.length > 0
        ? (structureCompletedCount / structureSections.length) * 100
        : 0;

    const hasQuoted = (quotedSystemsData?.length ?? 0) > 0;

    // AHA-6681: an UNSET preference is not a non-ducted choice — only a
    // deliberately chosen non-ducted pathway skips Equipment Preferences.
    // Existing Equipment itself only exists on the ducted/hybrid pathways
    // (behind the Yes answer), so it reads complete everywhere else.
    const systemPreferenceChosen = isNotEmpty(
      getProjectSettingValue('system_preference')
    );

    const hasSystemsUsingDuctedAccessoryRules = selectedSystems.some(
      usesDuctedAccessoryRules
    );

    const configurationStepComplete =
      fetchedConfigurations.length > 0 ||
      hasQuoted ||
      systemsToCompare.length > 0 ||
      hasSystemsUsingDuctedAccessoryRules;
    const configurationRequirementsMet = [configurationStepComplete];
    const configurationFraction =
      configurationRequirementsMet.length > 0
        ? (configurationRequirementsMet.filter((r) => r).length /
            configurationRequirementsMet.length) *
          100
        : 0;

    const chooseSystemsPrerequisiteComplete =
      isChooseSystemsPrerequisiteComplete({
        systemDesignLocked,
        pageProgress: {
          structure: structureFraction,
          configurations: configurationFraction
        }
      });

    // Equipment Details Next-check found compatible systems (AHA-6681
    // LES-4): either the kept or the alternatives side counts. Intersecting
    // with the CURRENT existing systems keeps stale cross-structure entries
    // inert.
    const hasCompatibleEquipmentResults =
      !ductedDesignPending &&
      chooseSystemsPrerequisiteComplete &&
      existingSystems.some(
        (s) =>
          combinedCompatibleTotal(compatibleResultsBySystem[s.uniqueId]) > 0
      );

    const accessoriesRequirementsMet: boolean[] = [];
    selectedSystems
      .filter((s) => !isSystemQuoted(s))
      .forEach((system) => {
        accessoriesRequirementsMet.push(
          quotableSystems[system.uniqueId]?.accessoriesReviewed ?? false
        );
      });
    // Ducted systems don't have quotable system records yet, so they're always false
    selectedDuctedSystems
      .filter((s) => s.quoteStatus !== 'sent')
      .forEach(() => {
        accessoriesRequirementsMet.push(false);
      });

    // quote page
    const quoteRequirementsMet = [
      selectedSystems.some((system) => system.quoteStatus === 'sent') ||
        hasQuoted
    ];

    // update page progress
    dispatch(
      setPageProgress(
        produce(pageProgress, (draft) => {
          const projectFraction =
            projectRequirementsMet.length > 0
              ? (projectRequirementsMet.filter((r) => r).length /
                  projectRequirementsMet.length) *
                100
              : 0;
          draft.project = hasQuoted ? 100 : projectFraction;

          if (hasQuoted) {
            draft.existingEquipment = 100;
            draft.equipmentPreferences = 100;
          } else if (!asksExistingEquipment) {
            draft.existingEquipment = systemPreferenceChosen ? 100 : 0;
            // A pathway that never shows Equipment Preferences (ductless)
            // completes it with the preference; every pathway that shows
            // the page (ducted, PTAC) goes through the gate.
            draft.equipmentPreferences =
              (systemPreferenceChosen &&
                !equipmentPreferencesPageVisible(systemPreference)) ||
              equipmentPreferencesGate
                ? 100
                : 0;
          } else if (projectType === HAS_EXISTING_EQUIPMENT_NO) {
            draft.existingEquipment = 100;
            draft.equipmentPreferences = equipmentPreferencesGate ? 100 : 0;
          } else if (showExistingEquipmentPage) {
            draft.existingEquipment = existingEquipmentInventoryGate ? 100 : 0;
            draft.equipmentPreferences = equipmentPreferencesGate ? 100 : 0;
          } else {
            draft.existingEquipment = 0;
            draft.equipmentPreferences = 0;
          }

          draft.structure = hasQuoted ? 100 : structureFraction;
          draft.configurations = configurationFraction;
          draft.chooseSystems =
            hasQuoted ||
            systemsToCompare.length > 0 ||
            hasSystemsUsingDuctedAccessoryRules ||
            hasCompatibleEquipmentResults
              ? 100
              : 0;
          const accessoriesFraction =
            accessoriesRequirementsMet.length > 0
              ? (accessoriesRequirementsMet.filter((r) => r).length /
                  accessoriesRequirementsMet.length) *
                100
              : 0;
          draft.accessories = hasQuoted ? 100 : accessoriesFraction;
          draft.quote =
            quoteRequirementsMet.length > 0
              ? (quoteRequirementsMet.filter((r) => r).length /
                  quoteRequirementsMet.length) *
                100
              : 0;
        })
      )
    );
  }, [
    locationData,
    structureTypeId,
    structureSubtypeId,
    attomData,
    structure,
    configurations,
    selectedSystems,
    selectedDuctedSystems,
    existingSystems,
    compatibleResultsBySystem,
    recommendationSelectionsBySystem,
    recommendationSelectionsLoaded,
    wantCompositionPicksBySystem,
    projectSettings,
    totalBTUs.cooling,
    structureAttachments,
    quotableSystems,
    manuallyCompletedSteps,
    systemsToCompare,
    fetchedConfigurations,
    ductedDesignPending,
    systemDesignLocked,
    quotedSystemsData,
    isSystemQuoted,
    leftoverSpaceIds
  ]);

  return null;
}
